import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { productService } from '../../services/productService';
import type { Product, CreateProductRequest, ProductPeriod, ProductCategory } from '../../types/product';
import type { Facility } from '../../types/facility';

interface ProductFormModalProps {
  product: Product | null;
  businessProfileId: string;
  facilities: Facility[];
  categories: ProductCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

const VAT_RATES = [0, 5, 8, 23];

const PERIOD_OPTIONS: { value: ProductPeriod; labelKey: string }[] = [
  { value: 'Days' as ProductPeriod, labelKey: 'products.period.days' },
  { value: 'Weeks' as ProductPeriod, labelKey: 'products.period.weeks' },
  { value: 'Months' as ProductPeriod, labelKey: 'products.period.months' },
  { value: 'Years' as ProductPeriod, labelKey: 'products.period.years' },
  { value: 'Lifetime' as ProductPeriod, labelKey: 'products.period.lifetime' },
];

export function ProductFormModal({
  product,
  businessProfileId,
  facilities,
  categories,
  onClose,
  onSuccess,
}: ProductFormModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [isSaving, setIsSaving] = useState(false);

  const defaultCategoryId = () => {
    const fallback = categories.find(c => c.name === 'Inne');
    return fallback?.id ?? categories[0]?.id ?? '';
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    priceNet: 0,
    priceGross: 0,
    vatRate: 23,
    usageCount: 1,
    period: 'Months' as ProductPeriod,
    periodCount: 1,
    payableInApp: true,
    payableWithTrainer: false,
    isActive: true,
    facilityIds: [] as string[],
    allFacilities: true,
    productCategoryId: defaultCategoryId(),
    isMetered: false,
    meteredUnitLabel: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const calculateGrossFromNet = (net: number, vatRate: number): number => {
    if (net <= 0) return 0;
    return Math.round(net * (1 + vatRate / 100) * 100) / 100;
  };

  const calculateNetFromGross = (gross: number, vatRate: number): number => {
    if (gross <= 0) return 0;
    return Math.round(gross / (1 + vatRate / 100) * 100) / 100;
  };

  const handleNetPriceChange = (value: number) => {
    setFormData(prev => ({
      ...prev,
      priceNet: value,
      priceGross: calculateGrossFromNet(value, formData.vatRate),
    }));
    if (errors.priceNet) {
      setErrors(prev => { const next = { ...prev }; delete next.priceNet; return next; });
    }
  };

  const handleGrossPriceChange = (value: number) => {
    setFormData(prev => ({
      ...prev,
      priceNet: calculateNetFromGross(value, formData.vatRate),
      priceGross: value,
    }));
    if (errors.priceNet) {
      setErrors(prev => { const next = { ...prev }; delete next.priceNet; return next; });
    }
  };

  const handleVatRateChange = (vatRate: number) => {
    setFormData(prev => ({
      ...prev,
      vatRate,
      priceGross: calculateGrossFromNet(prev.priceNet, vatRate),
    }));
  };

  // Initialize form with product data
  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title,
        subtitle: product.subtitle || '',
        description: product.description || '',
        priceNet: product.price,
        priceGross: product.grossPrice ?? calculateGrossFromNet(product.price, product.vatRate),
        vatRate: product.vatRate,
        usageCount: product.usage,
        period: product.period,
        periodCount: product.numOfPeriods,
        payableInApp: product.payableInApp,
        payableWithTrainer: product.payableWithTrainer,
        isActive: product.isActive,
        facilityIds: product.facilityIds || [],
        allFacilities: product.appliesToAllFacilities ?? (!product.facilityIds || product.facilityIds.length === 0),
        productCategoryId: product.productCategoryId || defaultCategoryId(),
        isMetered: product.isMetered,
        meteredUnitLabel: product.meteredUnitLabel || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const handleChange = (field: string, value: string | number | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleMeteredToggle = (isMetered: boolean) => {
    setFormData(prev => ({
      ...prev,
      isMetered,
      // A metered item credits exactly one use per purchased unit — the
      // quantity picked at checkout IS the usage count, so this field is
      // meaningless (and locked) once metered is on.
      usageCount: isMetered ? 1 : prev.usageCount,
    }));
    if (errors.meteredUnitLabel) {
      setErrors(prev => { const next = { ...prev }; delete next.meteredUnitLabel; return next; });
    }
  };

  const handleFacilityToggle = (facilityId: string) => {
    setFormData(prev => {
      const currentIds = prev.facilityIds;
      const newIds = currentIds.includes(facilityId)
        ? currentIds.filter(id => id !== facilityId)
        : [...currentIds, facilityId];
      return { ...prev, facilityIds: newIds };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('validation.required');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('validation.required');
    }
    if (formData.priceNet <= 0) {
      newErrors.priceNet = t('validation.mustBePositive');
    }
    if (formData.usageCount < -1 || formData.usageCount === 0) {
      newErrors.usageCount = t('validation.invalidUsageCount');
    }
    if (formData.period !== 'Lifetime' && formData.periodCount <= 0) {
      newErrors.periodCount = t('validation.mustBePositive');
    }
    if (formData.isMetered && !formData.meteredUnitLabel.trim()) {
      newErrors.meteredUnitLabel = t('validation.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || !token) return;

    setIsSaving(true);
    try {
      const data: CreateProductRequest = {
        title: formData.title,
        subtitle: formData.subtitle || undefined,
        description: formData.description,
        price: formData.priceNet,
        vatRate: formData.vatRate,
        grossPrice: formData.priceGross,
        usage: formData.usageCount,
        period: formData.period,
        numOfPeriods: formData.period === 'Lifetime' ? 0 : formData.periodCount,
        payableInApp: formData.payableInApp,
        payableWithTrainer: formData.payableWithTrainer,
        isActive: formData.isActive,
        appliesToAllFacilities: formData.allFacilities,
        facilityIds: formData.allFacilities ? undefined : formData.facilityIds,
        productCategoryId: formData.productCategoryId || undefined,
        isMetered: formData.isMetered,
        meteredUnitLabel: formData.isMetered ? formData.meteredUnitLabel.trim() : undefined,
      };

      if (product) {
        await productService.updateProduct(businessProfileId, product.id, data, token);
        showToast('success', t('products.updateSuccess'));
      } else {
        await productService.createProduct(businessProfileId, data, token);
        showToast('success', t('products.createSuccess'));
      }

      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save product';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-brand-primary-deep rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">
            {product ? t('products.editProduct') : t('products.addProduct')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Title and Subtitle */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('products.fields.title')} *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => handleChange('title', e.target.value)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                    errors.title ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                  placeholder={t('products.placeholders.title')}
                />
                {errors.title && <p className="mt-1 text-sm text-brand-warm">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('products.fields.subtitle')}
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={e => handleChange('subtitle', e.target.value)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                  placeholder={t('products.placeholders.subtitle')}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('products.fields.description')} *
              </label>
              <textarea
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                  errors.description ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                }`}
                placeholder={t('products.placeholders.description')}
              />
              {errors.description && <p className="mt-1 text-sm text-brand-warm">{errors.description}</p>}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('products.fields.category', 'Kategoria')} *
              </label>
              <select
                value={formData.productCategoryId}
                onChange={e => handleChange('productCategoryId', e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
              >
                {categories.length === 0 && <option value="">—</option>}
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Metered item toggle */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isMetered}
                  onChange={e => handleMeteredToggle(e.target.checked)}
                  className="w-4 h-4 rounded border-border-strong bg-surface-card text-brand-accent focus:ring-brand-accent/50"
                />
                <span className="text-white">{t('products.fields.isMetered', 'Pozycja naliczana ilościowo')}</span>
              </label>
              <p className="text-xs text-white/60">
                {t('products.hints.isMetered', 'Klient wybiera ilość przy zakupie (np. "1 strzał") — kwota = cena jednostkowa × ilość.')}
              </p>
              {formData.isMetered && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('products.fields.meteredUnitLabel', 'Etykieta jednostki')} *
                  </label>
                  <input
                    type="text"
                    value={formData.meteredUnitLabel}
                    onChange={e => handleChange('meteredUnitLabel', e.target.value)}
                    placeholder={t('products.placeholders.meteredUnitLabel', 'np. strzał')}
                    className={`w-full px-3 py-2 bg-surface-card border rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                      errors.meteredUnitLabel ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                    }`}
                  />
                  {errors.meteredUnitLabel && <p className="mt-1 text-sm text-brand-warm">{errors.meteredUnitLabel}</p>}
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {formData.isMetered ? t('products.fields.unitPrice', 'Cena za jednostkę') : t('products.fields.priceNet')} (PLN) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.priceNet || ''}
                  onChange={e => handleNetPriceChange(parseFloat(e.target.value) || 0)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                    errors.priceNet ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                />
                {errors.priceNet && <p className="mt-1 text-sm text-brand-warm">{errors.priceNet}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('products.fields.vatRate')}
                </label>
                <select
                  value={formData.vatRate}
                  onChange={e => handleVatRateChange(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                >
                  {VAT_RATES.map(rate => (
                    <option key={rate} value={rate}>{rate}%</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('products.fields.priceGross')} (PLN)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.priceGross || ''}
                  onChange={e => handleGrossPriceChange(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
            </div>

            {/* Usage and Period */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('products.fields.usageCount')}
                </label>
                <input
                  type="number"
                  min="-1"
                  value={formData.usageCount}
                  disabled={formData.isMetered}
                  onChange={e => handleChange('usageCount', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    errors.usageCount ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                  }`}
                />
                <p className="mt-1 text-xs text-white/60">
                  {formData.isMetered ? t('products.hints.usageCountMetered', 'Zawsze 1 — ilość wybiera klient przy zakupie') : t('products.hints.usageCount')}
                </p>
                {errors.usageCount && <p className="mt-1 text-sm text-brand-warm">{errors.usageCount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('products.fields.period')}
                </label>
                <select
                  value={formData.period}
                  onChange={e => handleChange('period', e.target.value)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                >
                  {PERIOD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </select>
              </div>

              {formData.period !== 'Lifetime' && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('products.fields.periodCount')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.periodCount}
                    onChange={e => handleChange('periodCount', parseInt(e.target.value) || 1)}
                    className={`w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                      errors.periodCount ? 'border-brand-warm focus:ring-red-500/50' : 'border-border-strong focus:ring-brand-primary/50'
                    }`}
                  />
                  {errors.periodCount && <p className="mt-1 text-sm text-brand-warm">{errors.periodCount}</p>}
                </div>
              )}
            </div>

            {/* Payment Options */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-ink-primary">
                {t('products.fields.paymentOptions')}
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.payableInApp}
                    onChange={e => handleChange('payableInApp', e.target.checked)}
                    className="w-4 h-4 rounded border-border-strong bg-surface-card text-brand-primary focus:ring-brand-primary/50"
                  />
                  <span className="text-white">{t('products.payableInApp')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.payableWithTrainer}
                    onChange={e => handleChange('payableWithTrainer', e.target.checked)}
                    className="w-4 h-4 rounded border-border-strong bg-surface-card text-brand-primary-deep focus:ring-brand-primary/50"
                  />
                  <span className="text-white">{t('products.payableWithTrainer')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={e => handleChange('isActive', e.target.checked)}
                    className="w-4 h-4 rounded border-border-strong bg-surface-card text-brand-confirm-deep focus:ring-brand-confirm/50"
                  />
                  <span className="text-white">{t('products.active')}</span>
                </label>
              </div>
            </div>

            {/* Facility Assignment */}
            {facilities.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-white/70">
                  {t('products.fields.facilities')}
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.allFacilities}
                    onChange={e => handleChange('allFacilities', e.target.checked)}
                    className="w-4 h-4 rounded border-border-strong bg-surface-card text-brand-primary focus:ring-brand-primary/50"
                  />
                  <span className="text-white">{t('products.allFacilities')}</span>
                </label>

                {!formData.allFacilities && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {facilities.map(facility => (
                      <label
                        key={facility.id}
                        className="flex items-center gap-2 p-2 bg-surface-card rounded-lg cursor-pointer hover:bg-surface-card-hover"
                      >
                        <input
                          type="checkbox"
                          checked={formData.facilityIds.includes(facility.id)}
                          onChange={() => handleFacilityToggle(facility.id)}
                          className="w-4 h-4 rounded border-border-strong bg-surface-card text-brand-primary focus:ring-brand-primary/50"
                        />
                        <span className="text-sm text-ink-primary truncate">{facility.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isSaving && (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            {product ? t('common.save') : t('common.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
